/**
 * Cookie Utilities for PLZ SSO Integration
 * Reads authentication tokens from shared cookies set by plz-admin
 * 
 * This follows the same pattern as joyz-helpdesk for seamless SSO
 */

/**
 * Cookie names - must match plz-admin / joyz-core CoreCookies enum
 */
export const CookieNames = {
  SESSION_TOKEN: 'sessionToken',
  REFRESH_TOKEN: 'refreshToken',
  LANG: 'lang',
} as const;

/**
 * Parsed cookie values
 */
export interface CookieTokens {
  sessionToken: string | null;
  refreshToken: string | null;
}

/**
 * Parses document.cookie string into a key-value object
 */
function parseCookies(): Record<string, string> {
  if (typeof document === 'undefined') {
    return {};
  }

  return document.cookie.split(';').reduce((acc, cookie) => {
    const [key, ...valueParts] = cookie.trim().split('=');
    if (key) {
      // Handle cookies with '=' in value
      acc[key] = valueParts.join('=');
    }
    return acc;
  }, {} as Record<string, string>);
}

/**
 * Gets a specific cookie value by name
 */
export function getCookie(name: string): string | null {
  const cookies = parseCookies();
  return cookies[name] || null;
}

/**
 * Gets authentication tokens from cookies
 * These are set by plz-admin on the shared domain
 */
export function getTokensFromCookies(): CookieTokens {
  return {
    sessionToken: getCookie(CookieNames.SESSION_TOKEN),
    refreshToken: getCookie(CookieNames.REFRESH_TOKEN),
  };
}

/**
 * Checks if authentication cookies exist
 */
export function hasAuthCookies(): boolean {
  const { sessionToken } = getTokensFromCookies();
  return sessionToken !== null && sessionToken.length > 0;
}

/**
 * Gets the current language from cookie (if set by plz-admin)
 */
export function getLanguageFromCookie(): string | null {
  return getCookie(CookieNames.LANG);
}

/**
 * Decodes JWT token payload without verification
 * Used to extract user info and expiration from the token
 * 
 * NOTE: This does NOT verify the token signature - that should be done by the backend
 */
export function decodeJwtPayload<T = Record<string, unknown>>(token: string): T | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    // Handle base64url encoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload) as T;
  } catch (error) {
    console.error('Failed to decode JWT payload:', error);
    return null;
  }
}

/**
 * JWT payload structure from Keycloak
 */
export interface KeycloakTokenPayload {
  exp: number; // Expiration time (Unix timestamp in seconds)
  iat: number; // Issued at
  jti: string; // JWT ID
  iss: string; // Issuer
  sub: string; // Subject (user ID)
  typ: string; // Token type
  azp: string; // Authorized party
  session_state: string;
  email?: string;
  email_verified?: boolean;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  HAIZID?: number; // User ID in our system
  crowd_id?: string; // Community ID
  crowd_name?: string; // Community name
  crowd_features?: Record<string, boolean>;
  realm_access?: {
    roles: string[];
  };
  resource_access?: Record<string, { roles: string[] }>;
}

/**
 * Extracts user information from the session token
 */
export function extractUserFromToken(sessionToken: string): {
  id: string;
  email: string;
  communityId: string;
  expiresAt: number;
} | null {
  const payload = decodeJwtPayload<KeycloakTokenPayload>(sessionToken);
  
  if (!payload) {
    return null;
  }

  return {
    id: payload.sub || payload.HAIZID?.toString() || '',
    email: payload.email || payload.preferred_username || '',
    communityId: payload.crowd_id || '',
    // Convert exp from seconds to milliseconds
    expiresAt: payload.exp * 1000,
  };
}

/**
 * Checks if a token is expired based on its exp claim
 */
export function isTokenExpiredFromJwt(token: string): boolean {
  const payload = decodeJwtPayload<KeycloakTokenPayload>(token);
  
  if (!payload || !payload.exp) {
    return true;
  }

  const now = Date.now();
  const expiresAt = payload.exp * 1000; // Convert to milliseconds
  const buffer = 30 * 1000; // 30 second buffer

  return expiresAt - buffer <= now;
}



